import {
  ROUTE_CERTIFICATES,
  ROUTE_CERTIFICATES_CREATE,
  ROUTE_CERTIFICATES_EDIT,
  ROUTE_NEWS,
  ROUTE_NEWS_CREATE,
  ROUTE_NEWS_EDIT,
  ROUTE_PORTAL_CREATOR,
  ROUTE_PORTAL_LAYOUT,
  ROUTE_PORTAL_LAYOUT_BUNDLE_EDIT,
  ROUTE_PORTAL_LAYOUT_CATEGORIES_REORDER,
  ROUTE_PORTAL_LAYOUT_CATEGORY_CREATE,
  ROUTE_PORTAL_LAYOUT_CATEGORY_EDIT,
  ROUTE_PORTAL_LAYOUT_FOR_YOU,
  ROUTE_PORTAL_LAYOUT_JOURNEY_CREATE,
  ROUTE_PORTAL_LAYOUT_JOURNEY_EDIT,
  ROUTE_PORTAL_LAYOUT_JOURNEYS,
} from '@/router/routes/routeNames'

const PortalLayout = () =>
  import('@/components/portal_creator/portal_layout/PortalLayoutView.vue')
const PortalLayoutCategories = () =>
  import(
    '@/components/portal_creator/portal_layout/PortalLayoutCategoriesView.vue'
  )
const CategoriesReorder = () =>
  import(
    '@/components/portal_creator/portal_layout/PortalLayoutCategoryReorderView.vue'
  )
const EditCategoryView = () =>
  import('@/components/portal_creator/portal_layout/EditCategoryView.vue')
const PortalLayoutCategoryCreate = () =>
  import(
    '@/components/portal_creator/portal_layout/category/PortalLayoutCategoryCreate.vue'
  )
const PortalLayoutBundleEdit = () =>
  import(
    '@/components/portal_creator/portal_layout/PortalLayoutBundleEditView.vue'
  )
const News = () => import('@/components/portal_creator/news/NewsView.vue')
const NewsPostEdit = () =>
  import('@/components/portal_creator/news/NewsPostEditView.vue')
const NewsPostCreate = () =>
  import('@/components/portal_creator/news/NewsPostCreateView.vue')
const CertificatesView = () =>
  import('@/components/portal_creator/certificates/CertificatesView.vue')
const CertificatesCreateView = () =>
  import('@/components/portal_creator/certificates/CertificatesCreateView.vue')
const CertificatesEditView = () =>
  import('@/components/portal_creator/certificates/CertificatesEditView.vue')

const portalCreatorRoutes = [
  {
    name: ROUTE_PORTAL_CREATOR,
    path: '/portal-creator',
    component: PortalLayout,
    redirect: { name: ROUTE_PORTAL_LAYOUT },
    props: true,
    meta: { resource: 'ProductCategories', requiresPortal: true },
  },
  {
    // The shell: top-level For you / Journey navigation, the filter bar and the
    // product pool. Each tab is a child route, so the two lists are separately
    // addressable. `?tab=` is still honoured so links shared before that
    // restructure keep working.
    name: ROUTE_PORTAL_LAYOUT,
    path: '/portal-creator/portal-layout',
    component: PortalLayout,
    props: true,
    meta: { resource: 'ProductCategories', requiresPortal: true },
    // `query: {}` drops the legacy `?tab=`, so the tab lives in the path only.
    redirect: (to) => ({
      name:
        to.query.tab === 'journey'
          ? ROUTE_PORTAL_LAYOUT_JOURNEYS
          : ROUTE_PORTAL_LAYOUT_FOR_YOU,
      query: {},
    }),
    children: [
      // `meta` is repeated on every child rather than inherited:
      // checkAccessForNavigation walks `route.matched` and reads each record's
      // own meta, so a child without `resource` fails the guard.
      {
        name: ROUTE_PORTAL_LAYOUT_FOR_YOU,
        path: 'for-you',
        component: PortalLayoutCategories,
        meta: { resource: 'ProductCategories', requiresPortal: true },
        children: [
          {
            // Rendered into the tab view's <router-view> as a modal, so the
            // category list stays mounted behind it.
            name: ROUTE_PORTAL_LAYOUT_CATEGORY_CREATE,
            path: 'create',
            component: PortalLayoutCategoryCreate,
            props: { isJourney: false },
            meta: { resource: 'ProductCategories', requiresPortal: true },
          },
        ],
      },
      {
        name: ROUTE_PORTAL_LAYOUT_JOURNEYS,
        path: 'journeys',
        component: PortalLayoutCategories,
        meta: { resource: 'ProductCategories', requiresPortal: true },
        children: [
          {
            name: ROUTE_PORTAL_LAYOUT_JOURNEY_CREATE,
            path: 'create',
            component: PortalLayoutCategoryCreate,
            props: { isJourney: true },
            meta: { resource: 'ProductCategories', requiresPortal: true },
          },
        ],
      },
      {
        // The create modal used to hang off the shell itself; keep those links
        // working.
        path: 'create',
        redirect: (to) => ({
          name:
            to.query.tab === 'journey'
              ? ROUTE_PORTAL_LAYOUT_JOURNEY_CREATE
              : ROUTE_PORTAL_LAYOUT_CATEGORY_CREATE,
          query: {},
        }),
      },
    ],
  },
  {
    name: ROUTE_PORTAL_LAYOUT_CATEGORIES_REORDER,
    path: '/portal-creator/portal_layout/reorder/:isJourney',
    component: CategoriesReorder,
    props: true,
    meta: { resource: 'ProductCategories', requiresPortal: true },
  },
  {
    name: ROUTE_PORTAL_LAYOUT_CATEGORY_EDIT,
    path: '/portal-creator/portal-layout/category/:categoryId',
    component: EditCategoryView,
    props: true,
    meta: { resource: 'ProductCategories', requiresPortal: true },
  },
  {
    name: ROUTE_PORTAL_LAYOUT_JOURNEY_EDIT,
    path: '/portal-creator/portal-layout/journey/:categoryId',
    component: EditCategoryView,
    props: true,
    meta: { resource: 'ProductCategories', requiresPortal: true },
  },
  {
    name: ROUTE_PORTAL_LAYOUT_BUNDLE_EDIT,
    path: '/portal-creator/portal-layout/category/:categoryId/bundle/:bundleId',
    component: PortalLayoutBundleEdit,
    props: true,
    meta: { resource: 'ProductCategories', requiresPortal: true },
  },
  {
    name: ROUTE_NEWS,
    path: '/portal-creator/news',
    component: News,
    meta: {
      resource: 'News',
      requiresNews: true,
      requiresPortal: true,
      showGlobalHeader: false,
    },
  },
  {
    name: ROUTE_NEWS_CREATE,
    path: '/portal-creator/news/create',
    component: NewsPostCreate,
    meta: {
      resource: 'News',
      requiresNews: true,
      requiresPortal: true,
      showGlobalHeader: false,
    },
  },
  {
    name: ROUTE_NEWS_EDIT,
    path: '/portal-creator/news/:newsPostId',
    component: NewsPostEdit,
    props: true,
    meta: {
      resource: 'News',
      requiresNews: true,
      requiresPortal: true,
      showGlobalHeader: false,
    },
  },
  {
    name: ROUTE_CERTIFICATES,
    path: '/portal-creator/certificates',
    component: CertificatesView,
    meta: {
      resource: 'Certificates',
      requiresPortal: true,
      showGlobalHeader: false,
    },
  },

  {
    name: ROUTE_CERTIFICATES_CREATE,
    path: '/portal-creator/certificates/create',
    component: CertificatesCreateView,
    meta: {
      resource: 'Certificates',
      requiresPortal: true,
      showGlobalHeader: false,
      maxWidthNarrow: true,
    },
  },
  {
    name: ROUTE_CERTIFICATES_EDIT,
    path: '/portal-creator/certificates/:certificateId',
    component: CertificatesEditView,
    props: true,
    meta: {
      resource: 'Certificates',
      requiresPortal: true,
      showGlobalHeader: false,
      maxWidthNarrow: true,
    },
  },
]

export default portalCreatorRoutes
